import ExcelJS from 'exceljs';
import moment from 'moment/moment';

import { fc } from 'i18n';
import { store } from 'core/redux/store';
import { localStorage } from 'core/app/storage';
import { ALL_TEP_COLUMNS_IDS } from 'constants/tep';
import { resolve } from 'core/misc/location/resolve';
import { IExportedTepEntryPass, ITepEntryPass } from 'types/tep';

interface IColumn {
  header: string;
  key: string;
  width: number;
}

const WORKSHEET_NAME = 'TEP management';

const HEADERS: Record<string, string> = {
  name: 'Device Name',
  'device.imei': 'Device IMEI',
  location: 'Location',
  'lease.company.name': 'Company',
  'lease.email': 'Email',
  'lease.plateNumber': 'Plate Number',
  'lease.startOn': 'TEP Start',
  'lease.endOn': 'TEP End',
  'lease.draftedAt': 'Drafted At',
  'lease.issuedAt': 'Issued At',
  status: 'Status',
};

const KEYS: Record<string, string> = {
  name: 'name',
  'device.imei': 'imei',
  location: 'location',
  'lease.company.name': 'company',
  'lease.email': 'email',
  'lease.plateNumber': 'plateNumber',
  'lease.startOn': 'startOn',
  'lease.endOn': 'endOn',
  'lease.draftedAt': 'draftedAt',
  'lease.issuedAt': 'issuedAt',
  status: 'status',
};

const COLORS = {
  headerBackground: 'FF4472C4',
  headerText: 'FFFFFFFF',
  availableStatus: 'ff1c7903',
  defaultText: 'ff000000',
  expiredDate: 'ffc92828',
};

const getFormattedDate = (date?: string): string => (date ? moment(date).format('YYYY-MM-DD') : '-');

const getVisibleColumns = (): string[] => {
  const hiddenColumns = localStorage.get('tem-management-columns') || [];

  return ALL_TEP_COLUMNS_IDS.filter((id) => !hiddenColumns.includes(id));
};

const getExportedColumns = () => {
  const visibleColumns = getVisibleColumns();

  return [...visibleColumns, 'location'].map((id) => ({ header: HEADERS[id], key: KEYS[id], width: 30 }));
};

const getLocationAddress = async (uri: string): Promise<string> => {
  try {
    const devicesStates = store.getState().deviceStates.map;
    const message = uri && devicesStates ? devicesStates[uri]?.message : null;
    const { longitude, latitude } = message || {};

    if (!longitude || !latitude) {
      return 'n/a';
    }

    const addressLocation = { longitude: longitude.toFixed(4), latitude: latitude.toFixed(4) };
    const fallbackCoordinates = `(${longitude.toFixed(6)}; ${latitude.toFixed(6)})`;

    return new Promise((resolvePromise) => {
      const resolveComplete = (entry: any) => {
        const addressString = entry.getAddress()?.format() || '';
        entry.removeObservers(addressLocation);
        resolvePromise(addressString || fallbackCoordinates);
      };

      const resolveInProgress = () => {
        // Do nothing, we're handling this with the promise
      };

      const entry = resolve(
        addressLocation.longitude,
        addressLocation.latitude,
        resolveComplete,
        resolveInProgress,
        addressLocation,
      );

      // If the entry is already ready, we can resolve immediately
      // This is a separate execution path from the resolveComplete callback
      if (entry && entry.ready()) {
        entry.removeObservers(addressLocation);
        resolvePromise(entry.getAddress()?.format() || fallbackCoordinates);
      }
    });
  } catch (error) {
    return 'n/a';
  }
};

const getFormattedData = async (data: ITepEntryPass[]): Promise<Partial<IExportedTepEntryPass>[]> => {
  const visibleColumns = getVisibleColumns();
  const formattedData: Partial<IExportedTepEntryPass>[] = [];
  const locationPromises: Promise<string>[] = [];

  for (const entryPass of data || []) {
    const formattedEntry: Partial<IExportedTepEntryPass> = {};

    if (visibleColumns.includes('name')) {
      formattedEntry.name = entryPass.name;
    }

    if (visibleColumns.includes('device.imei')) {
      formattedEntry.imei = entryPass.device.uri;
    }

    locationPromises.push(getLocationAddress(entryPass.device.uri));

    if (visibleColumns.includes('lease.company.name')) {
      formattedEntry.company = entryPass.lease?.company || '-';
    }

    if (visibleColumns.includes('lease.email')) {
      formattedEntry.email = entryPass.lease?.email || '-';
    }

    if (visibleColumns.includes('lease.plateNumber')) {
      formattedEntry.plateNumber = entryPass.lease?.plateNumber || '-';
    }

    if (visibleColumns.includes('lease.startOn')) {
      formattedEntry.startOn = getFormattedDate(entryPass.lease?.startOn);
    }

    if (visibleColumns.includes('lease.endOn')) {
      formattedEntry.endOn = getFormattedDate(entryPass.lease?.endOn);
    }

    if (visibleColumns.includes('lease.draftedAt')) {
      formattedEntry.draftedAt = getFormattedDate(entryPass.lease?.draftedAt);
    }

    if (visibleColumns.includes('lease.issuedAt')) {
      formattedEntry.issuedAt = getFormattedDate(entryPass.lease?.issuedAt);
    }

    if (visibleColumns.includes('status')) {
      formattedEntry.status = fc(entryPass.status.toLowerCase()) || '-';
    }

    formattedData.push(formattedEntry);
  }

  const resolvedLocations = await Promise.all(locationPromises.map((promise) => promise.catch(() => 'n/a')));

  formattedData.forEach((formattedEntry, index) => {
    formattedEntry.location = resolvedLocations[index];
  });

  return formattedData;
};

const createWorksheet = (
  workbook: ExcelJS.Workbook,
  formattedData: Partial<IExportedTepEntryPass>[],
  columns: IColumn[],
) => {
  const worksheet = workbook.addWorksheet(WORKSHEET_NAME);
  const isEndOn = !!columns.find((column) => column.key === 'endOn');
  const isStatus = !!columns.find((column) => column.key === 'status');

  worksheet.columns = columns;

  worksheet.getRow(1).eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBackground } };
    cell.font = { bold: true, color: { argb: COLORS.headerText } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  formattedData.forEach((entryPass) => {
    const row = worksheet.addRow(entryPass);

    if (isStatus) {
      const statusCell = row.getCell('status');

      statusCell.font = {
        color: {
          argb: entryPass.status === 'Available' ? COLORS.availableStatus : COLORS.defaultText,
        },
      };
    }

    if (isEndOn) {
      const endOnCell = row.getCell('endOn');
      const isExpired = entryPass.endOn && moment(entryPass.endOn).isBefore(new Date(), 'day');

      endOnCell.font = {
        color: {
          argb: isExpired ? COLORS.expiredDate : COLORS.defaultText,
        },
      };
    }
  });

  return { worksheet, isEndOn, isStatus };
};

const downloadFile = (blob: Blob, filename: string): void => {
  try {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up
  } catch (error) {
    throw new Error('Failed to download file');
  }
};

const exportXlsx = async (data: ITepEntryPass[]): Promise<void> => {
  try {
    const formattedData = await getFormattedData(data);
    const columns = getExportedColumns();
    const workbook = new ExcelJS.Workbook();

    createWorksheet(workbook, formattedData, columns);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    downloadFile(blob, `${WORKSHEET_NAME}.xlsx`);
  } catch (error) {
    throw new Error('Failed to export to XLSX');
  }
};

const exportCsv = async (data: ITepEntryPass[]): Promise<void> => {
  try {
    const formattedData = await getFormattedData(data);
    const columns = getExportedColumns();
    const workbook = new ExcelJS.Workbook();

    const { worksheet } = createWorksheet(workbook, formattedData, columns);

    let csvContent = '';

    worksheet.getRow(1).eachCell((cell) => {
      csvContent += `"${cell.text.replace(/"/g, '""')}"` + ',';
    });
    csvContent = csvContent.slice(0, -1) + '\n';

    for (let i = 2; i <= worksheet.rowCount; i++) {
      worksheet.getRow(i).eachCell((cell) => {
        csvContent += `"${cell.text.replace(/"/g, '""')}"` + ',';
      });
      csvContent = csvContent.slice(0, -1) + '\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    downloadFile(blob, `${WORKSHEET_NAME}.csv`);
  } catch (error) {
    throw new Error('Failed to export to CSV');
  }
};

export const exportTepManagement = async (format: string, data: ITepEntryPass[]): Promise<void> => {
  switch (format.toLowerCase()) {
    case 'xlsx':
      return await exportXlsx(data);
    case 'csv':
      return await exportCsv(data);
  }
};
